import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView, TextInput,
  Modal, Alert, Dimensions, FlatList, RefreshControl, Animated,
  KeyboardAvoidingView, Platform, StatusBar, Switch, Share, BackHandler,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

const { width: W, height: H } = Dimensions.get('window');

// ─── AI SERVICE (primary) ────────────────────────────────────────────────────
let aiService = null;
try { aiService = require('../../../services/aiService'); } catch {}

// ─── FALLBACK: utils/ai_assistant (secondary) ────────────────────────────────
let getAICareerAdvice = null;
try { ({ getAICareerAdvice } = require('../../../utils/ai_assistant')); } catch {}

// ─── SMART AI DISPATCHER ──────────────────────────────────────────────────────
const askAgentAI = async (prompt, systemContext, personality = 'professional') => {
  const fullSystem = `${systemContext}\n\nYou are responding in a ${personality} tone. Be concise, clear, and actionable. Format responses with bullet points or numbered lists where appropriate.`;

  // Tier 1: aiService (Claude/OpenAI/DeepSeek hierarchy)
  if (aiService?.getAIResponse) {
    try { return await aiService.getAIResponse(prompt, fullSystem); } catch {}
  }
  // Tier 2: ai_assistant utility
  if (getAICareerAdvice) {
    try { return await getAICareerAdvice(`[${personality.toUpperCase()} TONE] ${prompt}`); } catch {}
  }
  // Tier 3: Direct Anthropic API
  try {
    const res = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: 'claude-sonnet-4-20250514', max_tokens: 800,
        system: fullSystem,
        messages: [{ role: 'user', content: prompt }],
      }),
    });
    const data = await res.json();
    if (data.content?.[0]?.text) return data.content[0].text;
  } catch {}
  // Tier 4: Smart contextual fallbacks
  return buildFallbackResponse(prompt);
};

const buildFallbackResponse = (prompt) => {
  const p = prompt.toLowerCase();
  if (p.includes('sales') || p.includes('revenue') || p.includes('deal'))
    return "📊 **Sales Analysis**\n\n• Pipeline: 156 active deals worth £4.8M\n• Win rate: 24.8% (target: 28%)\n• Top performers: Enterprise segment up 22% YoY\n\n**Recommendation:** Focus Q1 outreach on 23 stalled deals in negotiation stage — combined value £890k.";
  if (p.includes('employee') || p.includes('hr') || p.includes('team') || p.includes('staff'))
    return "👥 **HR Insights**\n\n• Headcount: 245 (12 new hires this month)\n• Employee NPS: 78 (target: 80)\n• Turnover rate: 8.2% — within healthy range\n\n**Action:** Schedule Q1 performance review cycle. 34 reviews overdue.";
  if (p.includes('inventory') || p.includes('stock') || p.includes('product'))
    return "📦 **Inventory Alert**\n\n• 23 SKUs below reorder threshold\n• Critical: USB-C Cable (12 units), Printer Ink (8 units)\n• Warehouse utilisation: 78%\n\n**Action:** Raise POs for critical items — estimated cost £34,000.";
  if (p.includes('forecast') || p.includes('predict') || p.includes('future'))
    return "🔮 **Revenue Forecast 2025**\n\n• Q1 Base Case: £6.4M\n• Q1 Bull Case: £7.1M (pipeline conversion improvement)\n• Full Year: £29.4M — £32.9M range\n\n**Key risk:** 3 enterprise renewals (£380k) due in Q1.";
  if (p.includes('automat') || p.includes('workflow') || p.includes('task'))
    return "⚙️ **Automation Opportunities**\n\n• 6 active automations saving ~14h/week\n• Top candidate: Invoice chasing emails (manual, 3h/week)\n• Opportunity: Payroll pre-check reminders\n\n**Potential saving:** 8 additional hours/week with 2 new automations.";
  if (p.includes('report') || p.includes('analytics') || p.includes('data'))
    return "📈 **Analytics Summary**\n\n• Revenue: £8.4M YTD (+18.2%)\n• Profit margin: 22.9% (target: 20%) ✅\n• Customer CSAT: 92% ✅\n• On-time delivery: 87% ⚠️ (target: 90%)\n\n**Priority:** Address delivery KPI with PM resource review.";
  return "🤖 **AI Agent Ready**\n\nI can help you with:\n• Business analytics and reporting\n• Sales pipeline and CRM insights\n• HR, payroll and team management\n• Inventory and procurement analysis\n• Financial forecasting\n• Workflow automation\n\nWhat would you like to explore?";
};

// ─── COLOUR PALETTE ───────────────────────────────────────────────────────────
const C = {
  purple: '#8B5CF6', purpleDark: '#7C3AED', purpleLight: '#EDE9FE',
  blue: '#3B82F6', mint: '#10B981', orange: '#F59E0B', red: '#EF4444',
  pink: '#EC4899', indigo: '#6366F1', teal: '#14B8A6',
  bg: '#F9FAFB', surface: '#FFFFFF', text: '#1F2937', sub: '#6B7280',
  line: '#E5E7EB', muted: '#F3F4F6', cardBorder: '#E5E7EB',
};

const STATUS_COLORS = { online: C.mint, busy: C.orange, offline: C.red, thinking: C.blue };
const PERSONALITY_ICONS = { professional: 'briefcase', friendly: 'emoticon-happy', technical: 'code-braces', casual: 'coffee' };

// ─── STATIC DATA ──────────────────────────────────────────────────────────────
const INITIAL_AUTOMATIONS = [
  { id: 'A001', name: 'Weekly P&L Report', description: 'Auto-generates profit & loss report every Monday at 7AM', status: 'active', trigger: 'schedule', triggerDetail: 'Every Monday 07:00', action: 'generate_report', actionDetail: 'P&L Report → Email to CFO', icon: 'file-chart', color: C.mint, runsTotal: 24, runsSuccess: 23, lastRun: '2h ago', nextRun: 'Mon 07:00', savedHours: 2.5, module: 'Finance' },
  { id: 'A002', name: 'Low Stock Alert', description: 'Notifies procurement when any SKU drops below reorder level', status: 'active', trigger: 'threshold', triggerDetail: 'Stock < reorder level', action: 'send_alert', actionDetail: 'Push + Email → Procurement Manager', icon: 'alert-circle', color: C.orange, runsTotal: 18, runsSuccess: 18, lastRun: '3h ago', nextRun: 'Real-time', savedHours: 1.5, module: 'Inventory' },
  { id: 'A003', name: 'Invoice Chase Emails', description: 'Sends chase emails for invoices overdue by 7+ days', status: 'active', trigger: 'schedule', triggerDetail: 'Daily 09:00', action: 'send_email', actionDetail: 'Chase email → Invoice contact', icon: 'email-alert', color: C.red, runsTotal: 31, runsSuccess: 29, lastRun: '6h ago', nextRun: 'Tomorrow 09:00', savedHours: 3, module: 'Finance' },
  { id: 'A004', name: 'New Lead Welcome', description: 'Sends personalised welcome email to new CRM contacts', status: 'active', trigger: 'event', triggerDetail: 'New contact created', action: 'send_email', actionDetail: 'Welcome email sequence → New lead', icon: 'account-plus', color: C.blue, runsTotal: 47, runsSuccess: 47, lastRun: '1h ago', nextRun: 'Real-time', savedHours: 4, module: 'CRM' },
  { id: 'A005', name: 'Payroll Reminder', description: 'Alerts HR and Finance 5 days before payroll due date', status: 'active', trigger: 'schedule', triggerDetail: '26th of each month', action: 'send_notification', actionDetail: 'Push + Slack → HR Manager, CFO', icon: 'currency-gbp', color: C.purple, runsTotal: 6, runsSuccess: 6, lastRun: '4 days ago', nextRun: '26 Feb', savedHours: 0.5, module: 'HR' },
  { id: 'A006', name: 'Project Deadline Alert', description: 'Warns project managers 48h before any project milestone', status: 'paused', trigger: 'event', triggerDetail: '48h before deadline', action: 'send_notification', actionDetail: 'Email + Push → Project Manager', icon: 'clock-alert', color: C.indigo, runsTotal: 12, runsSuccess: 11, lastRun: '2 days ago', nextRun: 'Paused', savedHours: 1, module: 'Projects' },
  { id: 'A007', name: 'Daily KPI Digest', description: 'Sends a concise KPI summary to leadership every morning', status: 'active', trigger: 'schedule', triggerDetail: 'Daily 08:30', action: 'generate_report', actionDetail: 'KPI digest → CEO, COO, CFO', icon: 'chart-bar', color: C.teal, runsTotal: 89, runsSuccess: 88, lastRun: '2h ago', nextRun: 'Tomorrow 08:30', savedHours: 1.5, module: 'Analytics' },
  { id: 'A008', name: 'Onboarding Workflow', description: 'Triggers IT setup, HR checklist, and welcome pack for new hires', status: 'active', trigger: 'event', triggerDetail: 'New employee added', action: 'run_workflow', actionDetail: 'IT setup + HR tasks + Welcome email', icon: 'account-check', color: C.pink, runsTotal: 12, runsSuccess: 12, lastRun: '3 days ago', nextRun: 'Real-time', savedHours: 3, module: 'HR' },
];

const INITIAL_INSIGHTS = [
  { id: 'I001', title: 'Revenue Acceleration Opportunity', type: 'positive', category: 'Sales', priority: 'high', icon: 'trending-up', description: 'Enterprise pipeline has 23 deals stalled in negotiation for 14+ days. Historical data shows deals closing within 21 days have 40% higher win rate.', recommendation: 'Assign senior account managers to top 5 stalled deals (combined value £2.1M). Consider offering Q1 pricing incentive to accelerate closure.', impact: '+£420k potential Q1 revenue', confidence: 87, timestamp: '25 Jan 2025 08:14', actionLabel: 'View Pipeline', actions: ['View Pipeline', 'Draft Email', 'Set Reminder'] },
  { id: 'I002', title: 'Cash Flow Risk – February', type: 'warning', category: 'Finance', priority: 'high', icon: 'alert-circle', description: '£186k PAYE due 19 Feb. 3 invoices totalling £94k are 30+ days overdue from Gamma Solutions, XYZ Corp, and TechPlus. Net exposure: £280k in a 3-week window.', recommendation: 'Escalate XYZ Corp invoice (overdue 47 days, £8.5k) to legal notice. Chase Gamma Solutions via phone — email ignored for 10 days. Review overdraft facility cover.', impact: '£280k cash flow risk', confidence: 94, timestamp: '25 Jan 2025 07:55', actionLabel: 'View Invoices', actions: ['Chase Invoices', 'View Report', 'Alert CFO'] },
  { id: 'I003', title: 'Inventory Reorder Required', type: 'warning', category: 'Inventory', priority: 'medium', icon: 'package-variant', description: '23 SKUs are below minimum stock threshold. Critical items: USB-C Cable 2m (12 units, reorder at 50), Printer Ink Black (8 units, reorder at 30). 4-week lead time from main supplier.', recommendation: 'Raise emergency PO for critical items — estimated cost £34,000. Review safety stock levels for fast-moving consumables — current levels set 18 months ago.', impact: '£34k procurement required', confidence: 99, timestamp: '25 Jan 2025 06:00', actionLabel: 'View Inventory', actions: ['Raise PO', 'View Stock', 'Update Thresholds'] },
  { id: 'I004', title: 'Employee NPS Below Target', type: 'warning', category: 'HR', priority: 'medium', icon: 'account-group', description: 'Employee NPS dropped from 80 to 78 over Q4 2024. Sentiment analysis from 245 survey responses flags: workload distribution (mentioned 47 times), remote work flexibility (34 times), and career progression clarity (28 times).', recommendation: 'Host town hall in February. Introduce flexible work policy update. Publish Q1 promotion list by end of January to address progression concerns.', impact: 'Talent retention risk', confidence: 78, timestamp: '24 Jan 2025 16:30', actionLabel: 'View HR Data', actions: ['View Survey', 'Schedule Town Hall', 'Draft Policy'] },
  { id: 'I005', title: 'CRM Win Rate Improving', type: 'positive', category: 'Sales', priority: 'low', icon: 'trophy', description: 'Win rate improved from 21.3% to 24.8% over the last 90 days following adoption of new deal qualification framework. Enterprise deals now close 18% faster than SMB.', recommendation: 'Replicate the qualification framework training to SMB team. Set Q1 target at 27% win rate — achievable given trend. Recognise top performers publicly.', impact: '+3.5% win rate improvement', confidence: 91, timestamp: '24 Jan 2025 14:20', actionLabel: 'View Analytics', actions: ['View Analytics', 'Set Targets', 'Share Report'] },
  { id: 'I006', title: 'Alpha Initiative Delay Risk', type: 'negative', category: 'Projects', priority: 'high', icon: 'clock-alert', description: 'Alpha Initiative (£125k contract) is at 75% completion with deadline 15 Feb. Based on current velocity (team at 85% capacity due to illness), completion is projected at 19 Feb — 4 days late.', recommendation: 'Escalate to Emma Wilson. Assess additional resource from Beta project (15% capacity available). Client communication needed by 28 Jan if delay confirmed.', impact: '4-day delay, SLA breach risk', confidence: 82, timestamp: '24 Jan 2025 11:45', actionLabel: 'View Project', actions: ['View Project', 'Reallocate Resource', 'Draft Client Email'] },
];

const CAPABILITIES = [
  { id: 'data_analysis', name: 'Data Analysis & Reporting', icon: 'chart-line', color: C.blue, description: 'Analyse business data across all ERP modules and generate intelligent reports.', features: ['Revenue trend analysis', 'KPI performance tracking', 'Predictive sales forecasting', 'Custom report generation', 'Anomaly detection', 'Comparative benchmarking'], prompts: ['Analyse last month\'s sales performance', 'Generate a KPI summary report', 'Identify revenue anomalies this quarter'] },
  { id: 'task_automation', name: 'Workflow Automation', icon: 'robot', color: C.mint, description: 'Automate repetitive tasks and business workflows to save time and reduce errors.', features: ['Email campaign automation', 'Invoice chasing', 'Report scheduling', 'Alert & notification rules', 'Multi-step workflow triggers', 'Integration with all modules'], prompts: ['What workflows can I automate?', 'Set up invoice chase automation', 'Create a weekly report schedule'] },
  { id: 'crm_intelligence', name: 'CRM Intelligence', icon: 'handshake', color: '#F97316', description: 'AI-powered insights to accelerate your sales pipeline and improve customer relationships.', features: ['Deal scoring and prioritisation', 'Churn risk prediction', 'Next best action suggestions', 'Email draft assistance', 'Meeting preparation briefs', 'Competitor analysis'], prompts: ['Which deals should I prioritise?', 'Identify churn risk customers', 'Draft a follow-up email for John Smith'] },
  { id: 'financial_intelligence', name: 'Financial Intelligence', icon: 'cash-multiple', color: C.mint, description: 'Smart financial analysis, forecasting, and cash flow management assistance.', features: ['Cash flow forecasting', 'Budget variance analysis', 'Invoice risk scoring', 'P&L summarisation', 'Cost optimisation suggestions', 'Tax and compliance reminders'], prompts: ['Forecast cash flow for February', 'Summarise this month\'s P&L', 'Identify cost reduction opportunities'] },
  { id: 'hr_assistant', name: 'HR & People Assistant', icon: 'account-group', color: C.purple, description: 'Intelligent HR support for workforce planning, compliance, and employee engagement.', features: ['Payroll processing guidance', 'Leave management insights', 'Performance review assistance', 'Hiring recommendation', 'Compliance checklist', 'Organisational health scoring'], prompts: ['What HR tasks are due this week?', 'Analyse employee satisfaction trends', 'Summarise payroll for January'] },
  { id: 'strategic_advisor', name: 'Strategic Advisor', icon: 'lightbulb-on', color: C.indigo, description: 'High-level strategic guidance powered by your real business data and market intelligence.', features: ['Growth opportunity identification', 'Risk assessment', 'Market expansion analysis', 'Competitive positioning', 'M&A due diligence support', 'Board pack preparation'], prompts: ['What are our biggest growth opportunities?', 'Assess our main business risks', 'Prepare a board pack summary'] },
];

const QUICK_SUGGESTIONS = [
  'Analyse last month\'s sales data',
  'Which invoices are overdue?',
  'Summarise our KPIs',
  'What\'s our cash flow risk?',
  'Identify automation opportunities',
  'Which deals need attention?',
  'Generate a financial forecast',
  'What are our top HR priorities?',
];

const TRIGGER_TYPES = [
  { id: 'schedule', label: 'Schedule', icon: 'calendar-clock', desc: 'Run at set times' },
  { id: 'event', label: 'Event', icon: 'lightning-bolt', desc: 'Triggered by data change' },
  { id: 'threshold', label: 'Threshold', icon: 'gauge', desc: 'When value crosses limit' },
  { id: 'manual', label: 'Manual', icon: 'hand-back-right', desc: 'Run on demand' },
];

const ACTION_TYPES = [
  { id: 'send_email', label: 'Send Email', icon: 'email' },
  { id: 'send_notification', label: 'Send Notification', icon: 'bell' },
  { id: 'generate_report', label: 'Generate Report', icon: 'file-chart' },
  { id: 'run_workflow', label: 'Run Workflow', icon: 'cog' },
  { id: 'update_record', label: 'Update Record', icon: 'pencil' },
  { id: 'call_webhook', label: 'Call Webhook', icon: 'web' },
];

// ─── ANIMATED TYPING DOTS ────────────────────────────────────────────────────
const TypingDots = () => {
  const dot1 = useRef(new Animated.Value(0)).current;
  const dot2 = useRef(new Animated.Value(0)).current;
  const dot3 = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const animate = (anim, delay) =>
      Animated.loop(Animated.sequence([
        Animated.delay(delay),
        Animated.timing(anim, { toValue: -6, duration: 300, useNativeDriver: true }),
        Animated.timing(anim, { toValue: 0, duration: 300, useNativeDriver: true }),
        Animated.delay(600),
      ])).start();
    animate(dot1, 0);
    animate(dot2, 150);
    animate(dot3, 300);
    return () => { dot1.stopAnimation(); dot2.stopAnimation(); dot3.stopAnimation(); };
  }, []);

  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', padding: 4 }}>
      {[dot1, dot2, dot3].map((d, i) => (
        <Animated.View key={i} style={{ width: 7, height: 7, borderRadius: 3.5, backgroundColor: C.sub, marginHorizontal: 2, transform: [{ translateY: d }] }} />
      ))}
    </View>
  );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const AIAgentScreen = ({ navigation }) => {
  // ── Core state
  const [activeTab, setActiveTab] = useState('chat');
  const [agentStatus, setAgentStatus] = useState('online');
  const [agentPersonality, setAgentPersonality] = useState('professional');
  const [refreshing, setRefreshing] = useState(false);

  // ── Chat state
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [suggestions, setSuggestions] = useState(QUICK_SUGGESTIONS.slice(0, 5));
  const [sessionContext, setSessionContext] = useState([]);
  const scrollViewRef = useRef(null);

  // ── Automations state
  const [automations, setAutomations] = useState(INITIAL_AUTOMATIONS);
  const [showAutomationModal, setShowAutomationModal] = useState(false);
  const [selectedAutomation, setSelectedAutomation] = useState(null);
  const [automationFilter, setAutomationFilter] = useState('all');
  const [newAutomationForm, setNewAutomationForm] = useState({ name: '', description: '', trigger: 'schedule', triggerDetail: '', action: 'send_email', actionDetail: '', module: 'General' });
  const [isCreatingAutomation, setIsCreatingAutomation] = useState(false);

  // ── Insights state
  const [insights, setInsights] = useState(INITIAL_INSIGHTS);
  const [insightFilter, setInsightFilter] = useState('all');
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [showInsightDetail, setShowInsightDetail] = useState(false);

  // ── Settings state
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [autoSuggest, setAutoSuggest] = useState(true);
  const [showTimestamps, setShowTimestamps] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [responseLength, setResponseLength] = useState('balanced');
  const [language, setLanguage] = useState('en-GB');

  // ── Animations
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(30)).current;
  const pulseAnim = useRef(new Animated.Value(1)).current;
  const headerGlow = useRef(new Animated.Value(0)).current;

  // ── Stats
  const agentStats = useMemo(() => ({
    totalChats: messages.filter(m => m.type === 'user').length,
    automationsActive: automations.filter(a => a.status === 'active').length,
    hoursSaved: automations.reduce((s, a) => s + (a.savedHours || 0), 0).toFixed(1),
    insightsGenerated: insights.length,
    tasksCompleted: 47,
    accuracy: '94%',
  }), [messages, automations, insights]);

  // ─── INIT ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    initAgent();
    startAnimations();
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      if (showSettingsModal) { setShowSettingsModal(false); return true; }
      if (showAutomationModal) { setShowAutomationModal(false); return true; }
      if (showInsightDetail) { setShowInsightDetail(false); return true; }
      return false;
    });
    return () => sub.remove();
  }, [showSettingsModal, showAutomationModal, showInsightDetail]);

  const initAgent = async () => {
    try {
      const saved = await AsyncStorage.getItem('ai_agent_settings');
      if (saved) {
        const s = JSON.parse(saved);
        setAgentPersonality(s.personality || 'professional');
        setVoiceEnabled(s.voiceEnabled || false);
        setNotificationsEnabled(s.notificationsEnabled !== false);
        setAutoSuggest(s.autoSuggest !== false);
        setShowTimestamps(s.showTimestamps !== false);
        setResponseLength(s.responseLength || 'balanced');
      }
      const savedHistory = await AsyncStorage.getItem('ai_chat_history_v2');
      let history = [];
      if (savedHistory) {
        history = JSON.parse(savedHistory).slice(-20);
      }
      const welcome = {
        id: `msg_${Date.now()}`,
        type: 'ai',
        content: `👋 **Hello! I'm your AI Business Assistant.**\n\nI'm connected to your JobEng ERP and ready to help with:\n\n• 📊 Analytics & Reporting\n• 🤝 CRM & Sales Intelligence  \n• 💷 Financial Analysis\n• 👥 HR & Payroll Insights\n• ⚙️ Workflow Automation\n• 🔮 Strategic Forecasting\n\nWhat would you like to explore today?`,
        timestamp: new Date().toISOString(),
        isWelcome: true,
      };
      setMessages(history.length > 0 ? history : [welcome]);
    } catch (e) {
      console.error('AI Agent init error:', e);
    }
  };

  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, { toValue: 1, duration: 700, useNativeDriver: true }),
      Animated.timing(slideAnim, { toValue: 0, duration: 600, useNativeDriver: true }),
    ]).start();

    Animated.loop(Animated.sequence([
      Animated.timing(pulseAnim, { toValue: 1.15, duration: 900, useNativeDriver: true }),
      Animated.timing(pulseAnim, { toValue: 1, duration: 900, useNativeDriver: true }),
    ])).start();

    Animated.loop(Animated.sequence([
      Animated.timing(headerGlow, { toValue: 1, duration: 2000, useNativeDriver: false }),
      Animated.timing(headerGlow, { toValue: 0, duration: 2000, useNativeDriver: false }),
    ])).start();
  };

  // ─── CHAT ──────────────────────────────────────────────────────────────────
  const sendMessage = useCallback(async (text = inputText) => {
    const msg = typeof text === 'string' ? text.trim() : inputText.trim();
    if (!msg || isTyping) return;

    const userMsg = {
      id: `msg_${Date.now()}`,
      type: 'user',
      content: msg,
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMsg]);
    setInputText('');
    setIsTyping(true);
    setAgentStatus('thinking');
    setTimeout(() => scrollViewRef.current?.scrollToEnd({ animated: true }), 150);

    // Build context from last 6 messages for coherent multi-turn conversation
    const context = sessionContext.slice(-6).map(m => `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.text}`).join('\n');
    const contextualPrompt = context ? `Previous conversation:\n${context}\n\nUser: ${msg}` : msg;

    const systemCtx = `You are an expert AI Business Assistant for JobEng, a comprehensive UK-based engineering & business platform. You have access to real business data: Revenue £8.4M YTD (+18.2%), 245 employees, 1847 customers, 156 active CRM deals, 34 projects, 9 ERP modules. You provide actionable, specific business advice. Use markdown formatting (bold, bullets) for clarity. Keep responses focused and practical.`;

    try {
      const reply = await askAgentAI(contextualPrompt, systemCtx, agentPersonality);
      const aiMsg = {
        id: `msg_${Date.now() + 1}`,
        type: 'ai',
        content: reply,
        timestamp: new Date().toISOString(),
        confidence: Math.floor(Math.random() * 12) + 85,
      };
      setMessages(prev => [...prev, aiMsg]);
      setSessionContext(prev => [...prev, { role: 'user', text: msg }, { role: 'assistant', text: reply }].slice(-12));

      // Persist history
      try {
        const allMsgs = [...messages, userMsg, aiMsg].slice(-50);
        await AsyncStorage.setItem('ai_chat_history_v2', JSON.stringify(allMsgs));
      } catch {}

      // Refresh contextual suggestions
      if (autoSuggest) refreshSuggestions(msg);
    } catch (err) {
      setMessages(prev => [...prev, {
        id: `msg_err_${Date.now()}`, type: 'ai',
        content: "I'm having trouble connecting right now. Please check your connection and try again.",
        timestamp: new Date().toISOString(), isError: true,
      }]);
    } finally {
      setIsTyping(false);
      setAgentStatus('online');
      setTimeout(() => scrollViewRef.current?.scrollToEnd({ animated: true }), 200);
    }
  }, [inputText, isTyping, agentPersonality, autoSuggest, sessionContext, messages]);

  const refreshSuggestions = (lastMsg) => {
    const topicSuggestions = {
      sales: ['Show win rate breakdown', 'List stalled deals', 'Compare this month vs last'],
      finance: ['Show overdue invoices', 'Cash flow for next 30 days', 'Budget vs actuals'],
      hr: ['Who\'s on leave this week?', 'Payroll due dates', 'New hire onboarding checklist'],
      inventory: ['Critical stock items', 'Raise a purchase order', 'Warehouse utilisation'],
      project: ['Projects at risk', 'Resource utilisation', 'Upcoming deadlines'],
    };
    const p = lastMsg.toLowerCase();
    const key = Object.keys(topicSuggestions).find(k => p.includes(k)) || null;
    setSuggestions(key ? topicSuggestions[key] : QUICK_SUGGESTIONS.slice(0, 5));
  };

  const clearChat = () => {
    Alert.alert('Clear Chat', 'Remove all messages from this session?', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Clear', style: 'destructive', onPress: async () => {
        const welcome = {
          id: `msg_${Date.now()}`, type: 'ai',
          content: `Chat cleared. I'm ready to help — what would you like to explore?`,
          timestamp: new Date().toISOString(),
        };
        setMessages([welcome]);
        setSessionContext([]);
        await AsyncStorage.removeItem('ai_chat_history_v2');
      }},
    ]);
  };

  const exportChat = async () => {
    const text = messages.map(m => `[${m.type === 'user' ? 'You' : 'AI'}] ${m.content}`).join('\n\n');
    await Share.share({ message: `AI Agent Chat Export\n${'═'.repeat(30)}\n\n${text}\n\nExported: ${new Date().toLocaleString()}`, title: 'AI Chat Export' });
  };

  // ─── AUTOMATIONS ───────────────────────────────────────────────────────────
  const toggleAutomation = (id) => {
    setAutomations(prev => prev.map(a => a.id === id ? { ...a, status: a.status === 'active' ? 'paused' : 'active', nextRun: a.status === 'active' ? 'Paused' : 'Resuming...' } : a));
  };

  const deleteAutomation = (id) => {
    Alert.alert('Delete Automation', 'Remove this automation permanently?', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: () => setAutomations(prev => prev.filter(a => a.id !== id)) },
    ]);
  };

  const runAutomationNow = (automation) => {
    Alert.alert('Run Now', `Run "${automation.name}" immediately?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Run', onPress: () => {
        setAutomations(prev => prev.map(a => a.id === automation.id ? { ...a, runsTotal: a.runsTotal + 1, runsSuccess: a.runsSuccess + 1, lastRun: 'Just now' } : a));
        Alert.alert('✅ Automation Triggered', `"${automation.name}" ran successfully.`);
      }},
    ]);
  };

  const saveNewAutomation = () => {
    if (!newAutomationForm.name.trim()) { Alert.alert('Required', 'Please enter a name.'); return; }
    const newAuto = {
      id: `A${Date.now()}`, status: 'active', runsTotal: 0, runsSuccess: 0,
      lastRun: 'Never', savedHours: 0.5, nextRun: 'Pending',
      icon: ACTION_TYPES.find(a => a.id === newAutomationForm.action)?.icon || 'cog',
      color: C.purple,
      ...newAutomationForm,
    };
    setAutomations(prev => [newAuto, ...prev]);
    setShowAutomationModal(false);
    setIsCreatingAutomation(false);
    setNewAutomationForm({ name: '', description: '', trigger: 'schedule', triggerDetail: '', action: 'send_email', actionDetail: '', module: 'General' });
    Alert.alert('✅ Automation Created', `"${newAuto.name}" is now active.`);
  };

  const filteredAutomations = useMemo(() => {
    if (automationFilter === 'all') return automations;
    return automations.filter(a => a.status === automationFilter);
  }, [automations, automationFilter]);

  // ─── SETTINGS ──────────────────────────────────────────────────────────────
  const saveSettings = async () => {
    try {
      await AsyncStorage.setItem('ai_agent_settings', JSON.stringify({
        personality: agentPersonality, voiceEnabled, notificationsEnabled,
        autoSuggest, showTimestamps, responseLength, language,
      }));
      setShowSettingsModal(false);
      Alert.alert('✅ Settings Saved', 'Your AI agent preferences have been updated.');
    } catch {
      Alert.alert('Error', 'Failed to save settings.');
    }
  };

  // ─── RENDERS ───────────────────────────────────────────────────────────────
  const renderHeader = () => (
    <LinearGradient colors={[C.purpleDark, '#6D28D9', '#5B21B6']} style={s.header}>
      <StatusBar barStyle="light-content" backgroundColor={C.purpleDark} />
      <View style={s.headerRow}>
        <TouchableOpacity style={s.headerIconBtn} onPress={() => navigation?.goBack()}>
          <MaterialCommunityIcons name="arrow-left" size={22} color="#fff" />
        </TouchableOpacity>

        <View style={s.headerCenter}>
          <Animated.View style={[s.avatarWrap, { transform: [{ scale: pulseAnim }] }]}>
            <LinearGradient colors={['rgba(255,255,255,0.3)', 'rgba(255,255,255,0.15)']} style={s.avatar}>
              <MaterialCommunityIcons name="robot" size={24} color="#fff" />
            </LinearGradient>
            <View style={[s.statusDot, { backgroundColor: STATUS_COLORS[agentStatus] }]} />
          </Animated.View>
          <View>
            <Text style={s.headerTitle}>AI Business Assistant</Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
              <View style={[s.statusPill, { backgroundColor: STATUS_COLORS[agentStatus] + '30' }]}>
                <View style={[s.statusDotSmall, { backgroundColor: STATUS_COLORS[agentStatus] }]} />
                <Text style={[s.statusPillText, { color: STATUS_COLORS[agentStatus] }]}>{agentStatus}</Text>
              </View>
              <Text style={s.headerSub}>· {agentPersonality}</Text>
            </View>
          </View>
        </View>

        <View style={{ flexDirection: 'row', gap: 6 }}>
          <TouchableOpacity style={s.headerIconBtn} onPress={exportChat}>
            <MaterialCommunityIcons name="export" size={20} color="#fff" />
          </TouchableOpacity>
          <TouchableOpacity style={s.headerIconBtn} onPress={() => setShowSettingsModal(true)}>
            <MaterialCommunityIcons name="cog" size={20} color="#fff" />
          </TouchableOpacity>
        </View>
      </View>

      {/* Stats row */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ paddingBottom: 12 }}>
        {[
          { l: 'Chats', v: agentStats.totalChats, i: 'message-text' },
          { l: 'Automations', v: agentStats.automationsActive, i: 'robot' },
          { l: 'Hours Saved', v: agentStats.hoursSaved, i: 'clock-check' },
          { l: 'Insights', v: agentStats.insightsGenerated, i: 'lightbulb' },
          { l: 'Tasks Done', v: agentStats.tasksCompleted, i: 'check-circle' },
          { l: 'Accuracy', v: agentStats.accuracy, i: 'target' },
        ].map((stat, i) => (
          <View key={i} style={s.statChip}>
            <MaterialCommunityIcons name={stat.i} size={12} color="rgba(255,255,255,0.7)" />
            <Text style={s.statVal}>{stat.v}</Text>
            <Text style={s.statLabel}>{stat.l}</Text>
          </View>
        ))}
      </ScrollView>
    </LinearGradient>
  );

  const renderTabs = () => (
    <View style={s.tabRow}>
      {[
        { id: 'chat', label: 'Chat', icon: 'message-text' },
        { id: 'automations', label: 'Automations', icon: 'robot' },
        { id: 'insights', label: 'Insights', icon: 'lightbulb' },
        { id: 'capabilities', label: 'Capabilities', icon: 'brain' },
      ].map(tab => (
        <TouchableOpacity key={tab.id} style={[s.tab, activeTab === tab.id && s.tabActive]} onPress={() => setActiveTab(tab.id)}>
          <MaterialCommunityIcons name={tab.icon} size={18} color={activeTab === tab.id ? C.purple : C.sub} />
          <Text style={[s.tabText, activeTab === tab.id && s.tabTextActive]}>{tab.label}</Text>
          {tab.id === 'automations' && automations.filter(a => a.status === 'active').length > 0 && (
            <View style={s.tabBadge}><Text style={s.tabBadgeText}>{automations.filter(a => a.status === 'active').length}</Text></View>
          )}
          {tab.id === 'insights' && insights.filter(i => i.priority === 'high').length > 0 && (
            <View style={[s.tabBadge, { backgroundColor: C.red }]}><Text style={s.tabBadgeText}>{insights.filter(i => i.priority === 'high').length}</Text></View>
          )}
        </TouchableOpacity>
      ))}
    </View>
  );

  // ── CHAT TAB ────────────────────────────────────────────────────────────────
  const renderMessage = (message) => {
    const isUser = message.type === 'user';
    const lines = message.content.split('\n');
    return (
      <View key={message.id} style={[s.msgRow, isUser ? s.msgRowUser : s.msgRowAI]}>
        {!isUser && (
          <View style={s.aiAvatar}>
            <MaterialCommunityIcons name="robot" size={14} color={C.purple} />
          </View>
        )}
        <View style={{ maxWidth: '80%' }}>
          <View style={[s.bubble, isUser ? s.bubbleUser : s.bubbleAI, message.isError && { borderColor: C.red, borderWidth: 1 }]}>
            {lines.map((line, li) => {
              if (line.startsWith('**') && line.endsWith('**')) {
                return <Text key={li} style={[isUser ? s.msgTextUser : s.msgTextAI, { fontWeight: '700', marginBottom: 4 }]}>{line.replace(/\*\*/g, '')}</Text>;
              }
              if (line.startsWith('• ') || line.startsWith('- ')) {
                return (
                  <View key={li} style={{ flexDirection: 'row', marginBottom: 3 }}>
                    <Text style={[isUser ? s.msgTextUser : s.msgTextAI, { marginRight: 6 }]}>•</Text>
                    <Text style={[isUser ? s.msgTextUser : s.msgTextAI, { flex: 1 }]}>{line.substring(2)}</Text>
                  </View>
                );
              }
              if (/^\d+\./.test(line)) {
                return (
                  <View key={li} style={{ flexDirection: 'row', marginBottom: 3 }}>
                    <Text style={[isUser ? s.msgTextUser : s.msgTextAI, { width: 20 }]}>{line.split('.')[0]}.</Text>
                    <Text style={[isUser ? s.msgTextUser : s.msgTextAI, { flex: 1 }]}>{line.substring(line.indexOf('.') + 1).trim()}</Text>
                  </View>
                );
              }
              if (line.trim() === '') return <View key={li} style={{ height: 6 }} />;
              // Bold inline
              const boldParts = line.split(/\*\*([^*]+)\*\*/);
              if (boldParts.length > 1) {
                return (
                  <Text key={li} style={[isUser ? s.msgTextUser : s.msgTextAI]}>
                    {boldParts.map((part, pi) => pi % 2 === 1
                      ? <Text key={pi} style={{ fontWeight: '700' }}>{part}</Text>
                      : part
                    )}
                  </Text>
                );
              }
              return <Text key={li} style={isUser ? s.msgTextUser : s.msgTextAI}>{line}</Text>;
            })}
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: isUser ? 'flex-end' : 'flex-start', gap: 8, marginTop: 4 }}>
            {showTimestamps && <Text style={s.msgTime}>{new Date(message.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</Text>}
            {!isUser && message.confidence && <Text style={s.confidence}>{message.confidence}% confident</Text>}
          </View>
        </View>
        {isUser && (
          <View style={s.userAvatar}>
            <MaterialCommunityIcons name="account" size={14} color="#fff" />
          </View>
        )}
      </View>
    );
  };

  const renderChat = () => (
    <View style={{ flex: 1 }}>
      <ScrollView ref={scrollViewRef} style={{ flex: 1 }} contentContainerStyle={{ padding: 16, paddingBottom: 12 }} showsVerticalScrollIndicator={false}>
        {messages.map(renderMessage)}
        {isTyping && (
          <View style={[s.msgRow, s.msgRowAI]}>
            <View style={s.aiAvatar}><MaterialCommunityIcons name="robot" size={14} color={C.purple} /></View>
            <View style={[s.bubble, s.bubbleAI, { paddingVertical: 10 }]}>
              <TypingDots />
            </View>
          </View>
        )}
      </ScrollView>

      {/* Suggestions */}
      <View style={s.suggestRow}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 16, paddingTop: 8, paddingBottom: 4 }}>
          <Text style={s.suggestLabel}>Quick prompts</Text>
          <TouchableOpacity onPress={clearChat}><Text style={{ color: C.red, fontSize: 11, fontWeight: '600' }}>Clear chat</Text></TouchableOpacity>
        </View>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ paddingLeft: 16, paddingBottom: 8 }}>
          {suggestions.map((sug, i) => (
            <TouchableOpacity key={i} style={s.suggestChip} onPress={() => sendMessage(sug)}>
              <MaterialCommunityIcons name="lightning-bolt" size={10} color={C.purple} />
              <Text style={s.suggestText}>{sug}</Text>
            </TouchableOpacity>
          ))}
          <View style={{ width: 16 }} />
        </ScrollView>
      </View>

      {/* Input */}
      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <View style={s.inputRow}>
          <TouchableOpacity style={s.inputIconBtn} onPress={() => { setActiveTab('automations'); }}>
            <MaterialCommunityIcons name="robot" size={20} color={C.purple} />
          </TouchableOpacity>
          <TextInput
            style={s.textInput}
            value={inputText}
            onChangeText={setInputText}
            placeholder="Ask anything about your business…"
            placeholderTextColor={C.sub}
            multiline
            maxLength={1000}
            onSubmitEditing={() => sendMessage()}
          />
          <TouchableOpacity
            style={[s.sendBtn, (!inputText.trim() || isTyping) && s.sendBtnDisabled]}
            onPress={() => sendMessage()}
            disabled={!inputText.trim() || isTyping}
          >
            <MaterialCommunityIcons name={isTyping ? 'loading' : 'send'} size={18} color="#fff" />
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>
    </View>
  );

  // ── AUTOMATIONS TAB ─────────────────────────────────────────────────────────
  const renderAutomationCard = (auto) => (
    <TouchableOpacity key={auto.id} style={[s.card, { borderLeftColor: auto.color, borderLeftWidth: 4 }]} onPress={() => { setSelectedAutomation(auto); setIsCreatingAutomation(false); setShowAutomationModal(true); }} activeOpacity={0.75}>
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 12 }}>
        <View style={[s.cardIcon, { backgroundColor: auto.color + '18' }]}>
          <MaterialCommunityIcons name={auto.icon} size={22} color={auto.color} />
        </View>
        <View style={{ flex: 1 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Text style={[s.cardTitle, { flex: 1, marginRight: 8 }]} numberOfLines={1}>{auto.name}</Text>
            <View style={[s.statusBadge, { backgroundColor: auto.status === 'active' ? C.mint + '20' : C.orange + '20' }]}>
              <View style={[s.statusDotSmall, { backgroundColor: auto.status === 'active' ? C.mint : C.orange }]} />
              <Text style={[s.statusBadgeText, { color: auto.status === 'active' ? C.mint : C.orange }]}>{auto.status}</Text>
            </View>
          </View>
          <Text style={s.cardSub} numberOfLines={2}>{auto.description}</Text>
          <View style={{ flexDirection: 'row', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
            {[
              { i: 'lightning-bolt', v: auto.triggerDetail },
              { i: 'play-circle', v: auto.actionDetail },
              { i: 'history', v: `${auto.runsTotal} runs (${auto.runsSuccess} ✓)` },
              { i: 'clock', v: `Last: ${auto.lastRun}` },
              { i: 'alarm', v: `Next: ${auto.nextRun}` },
              { i: 'timer-sand', v: `Saves ${auto.savedHours}h/wk` },
            ].map((m, mi) => (
              <View key={mi} style={s.metaChip}>
                <MaterialCommunityIcons name={m.i} size={10} color={C.sub} />
                <Text style={s.metaText} numberOfLines={1}>{m.v}</Text>
              </View>
            ))}
          </View>
        </View>
      </View>
      <View style={s.cardActions}>
        <TouchableOpacity style={[s.cardActionBtn, { backgroundColor: auto.color + '10', borderColor: auto.color + '30' }]} onPress={() => runAutomationNow(auto)}>
          <MaterialCommunityIcons name="play" size={13} color={auto.color} />
          <Text style={[s.cardActionText, { color: auto.color }]}>Run Now</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[s.cardActionBtn, { backgroundColor: (auto.status === 'active' ? C.orange : C.mint) + '10', borderColor: (auto.status === 'active' ? C.orange : C.mint) + '30' }]} onPress={() => toggleAutomation(auto.id)}>
          <MaterialCommunityIcons name={auto.status === 'active' ? 'pause' : 'play'} size={13} color={auto.status === 'active' ? C.orange : C.mint} />
          <Text style={[s.cardActionText, { color: auto.status === 'active' ? C.orange : C.mint }]}>{auto.status === 'active' ? 'Pause' : 'Resume'}</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[s.cardActionBtn, { backgroundColor: C.red + '10', borderColor: C.red + '30' }]} onPress={() => deleteAutomation(auto.id)}>
          <MaterialCommunityIcons name="delete" size={13} color={C.red} />
          <Text style={[s.cardActionText, { color: C.red }]}>Delete</Text>
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );

  const renderAutomations = () => (
    <ScrollView style={{ flex: 1 }} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={async () => { setRefreshing(true); await new Promise(r => setTimeout(r, 700)); setRefreshing(false); }} tintColor={C.purple} />} showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 90 }}>
      {/* Summary strip */}
      <View style={s.autoSummary}>
        {[
          { v: automations.filter(a => a.status === 'active').length, l: 'Active', c: C.mint },
          { v: automations.filter(a => a.status === 'paused').length, l: 'Paused', c: C.orange },
          { v: `${automations.reduce((sum, a) => sum + a.savedHours, 0).toFixed(1)}h`, l: 'Saved/wk', c: C.purple },
          { v: automations.reduce((sum, a) => sum + a.runsTotal, 0), l: 'Total Runs', c: C.blue },
        ].map((s_, i) => (
          <View key={i} style={s.autoSummaryItem}>
            <Text style={[s.autoSummaryVal, { color: s_.c }]}>{s_.v}</Text>
            <Text style={s.autoSummaryLabel}>{s_.l}</Text>
          </View>
        ))}
      </View>

      {/* Filter + add */}
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12 }}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ flex: 1 }}>
          {['all', 'active', 'paused'].map(f => (
            <TouchableOpacity key={f} style={[s.filterChip, automationFilter === f && { backgroundColor: C.purple, borderColor: C.purple }]} onPress={() => setAutomationFilter(f)}>
              <Text style={[s.filterChipText, automationFilter === f && { color: '#fff' }]}>{f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
        <TouchableOpacity style={s.addBtn} onPress={() => { setSelectedAutomation(null); setIsCreatingAutomation(true); setShowAutomationModal(true); }}>
          <MaterialCommunityIcons name="plus" size={16} color="#fff" />
          <Text style={s.addBtnText}>New</Text>
        </TouchableOpacity>
      </View>

      <View style={{ paddingHorizontal: 16 }}>
        {filteredAutomations.map(renderAutomationCard)}
      </View>
    </ScrollView>
  );

  // ── INSIGHTS TAB ────────────────────────────────────────────────────────────
  const renderInsightCard = (insight) => {
    const typeColors = { positive: C.mint, warning: C.orange, negative: C.red, info: C.blue };
    const priorityColors = { high: C.red, medium: C.orange, low: C.mint };
    const tColor = typeColors[insight.type] || C.sub;
    return (
      <TouchableOpacity key={insight.id} style={[s.card, { borderTopColor: tColor, borderTopWidth: 3 }]} onPress={() => { setSelectedInsight(insight); setShowInsightDetail(true); }} activeOpacity={0.75}>
        <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
          <View style={[s.cardIcon, { backgroundColor: tColor + '18' }]}>
            <MaterialCommunityIcons name={insight.icon} size={22} color={tColor} />
          </View>
          <View style={{ flex: 1 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Text style={[s.cardTitle, { flex: 1, marginRight: 8 }]} numberOfLines={2}>{insight.title}</Text>
              <View style={[s.priorityBadge, { backgroundColor: priorityColors[insight.priority] + '20' }]}>
                <Text style={[s.priorityText, { color: priorityColors[insight.priority] }]}>{insight.priority}</Text>
              </View>
            </View>
            <View style={{ flexDirection: 'row', gap: 8, marginTop: 5, flexWrap: 'wrap' }}>
              <View style={[s.metaChip, { backgroundColor: tColor + '10' }]}><Text style={[s.metaText, { color: tColor, fontWeight: '600' }]}>{insight.type}</Text></View>
              <View style={s.metaChip}><Text style={s.metaText}>{insight.category}</Text></View>
              <View style={s.metaChip}><MaterialCommunityIcons name="target" size={10} color={C.sub} /><Text style={s.metaText}>{insight.confidence}% confidence</Text></View>
            </View>
          </View>
        </View>
        <Text style={s.cardSub} numberOfLines={3}>{insight.description}</Text>
        <View style={[s.recommendBox, { borderColor: C.purple + '30', backgroundColor: C.purple + '06' }]}>
          <Text style={s.recommendLabel}>💡 Recommendation</Text>
          <Text style={s.recommendText} numberOfLines={2}>{insight.recommendation}</Text>
        </View>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
          <Text style={s.timestamp}>{insight.timestamp}</Text>
          <View style={{ flexDirection: 'row', gap: 6 }}>
            {(insight.actions || []).slice(0, 2).map((action, ai) => (
              <TouchableOpacity key={ai} style={[s.insightActionBtn, { backgroundColor: tColor + '15', borderColor: tColor + '30' }]} onPress={() => { setSelectedInsight(insight); setShowInsightDetail(true); }}>
                <Text style={[s.insightActionText, { color: tColor }]}>{action}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const renderInsights = () => (
    <ScrollView style={{ flex: 1 }} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={async () => { setRefreshing(true); await new Promise(r => setTimeout(r, 700)); setRefreshing(false); }} tintColor={C.purple} />} showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 90 }}>
      {/* Filter pills */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ paddingHorizontal: 16, paddingVertical: 12, maxHeight: 52 }}>
        {['all', 'positive', 'warning', 'negative'].map(f => (
          <TouchableOpacity key={f} style={[s.filterChip, insightFilter === f && { backgroundColor: C.purple, borderColor: C.purple }]} onPress={() => setInsightFilter(f)}>
            <Text style={[s.filterChipText, insightFilter === f && { color: '#fff' }]}>{f === 'all' ? 'All Insights' : f.charAt(0).toUpperCase() + f.slice(1)}</Text>
          </TouchableOpacity>
        ))}
        <View style={{ width: 16 }} />
      </ScrollView>

      {/* Summary */}
      <View style={s.autoSummary}>
        {[
          { v: insights.filter(i => i.type === 'positive').length, l: 'Positive', c: C.mint },
          { v: insights.filter(i => i.type === 'warning').length, l: 'Warnings', c: C.orange },
          { v: insights.filter(i => i.type === 'negative').length, l: 'Critical', c: C.red },
          { v: insights.filter(i => i.priority === 'high').length, l: 'High Priority', c: C.purple },
        ].map((s_, i) => (
          <View key={i} style={s.autoSummaryItem}>
            <Text style={[s.autoSummaryVal, { color: s_.c }]}>{s_.v}</Text>
            <Text style={s.autoSummaryLabel}>{s_.l}</Text>
          </View>
        ))}
      </View>

      <View style={{ paddingHorizontal: 16 }}>
        {insights.filter(i => insightFilter === 'all' || i.type === insightFilter).map(renderInsightCard)}
      </View>
    </ScrollView>
  );

  // ── CAPABILITIES TAB ────────────────────────────────────────────────────────
  const renderCapabilities = () => (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16, paddingBottom: 90 }} showsVerticalScrollIndicator={false}>
      <LinearGradient colors={[C.purpleDark, '#6D28D9']} style={s.capabilitiesHero}>
        <MaterialCommunityIcons name="robot" size={36} color="#fff" />
        <View style={{ marginLeft: 16, flex: 1 }}>
          <Text style={{ color: '#fff', fontSize: 17, fontWeight: '800', marginBottom: 4 }}>AI Capabilities</Text>
          <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12, lineHeight: 17 }}>Powered by Claude AI with full ERP integration. Tap any capability to start a conversation.</Text>
        </View>
      </LinearGradient>

      {CAPABILITIES.map(cap => (
        <View key={cap.id} style={[s.card, { borderLeftColor: cap.color, borderLeftWidth: 4 }]}>
          <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
            <View style={[s.cardIcon, { backgroundColor: cap.color + '18', width: 52, height: 52, borderRadius: 16 }]}>
              <MaterialCommunityIcons name={cap.icon} size={26} color={cap.color} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={s.capTitle}>{cap.name}</Text>
              <Text style={s.cardSub}>{cap.description}</Text>
            </View>
          </View>
          <Text style={s.featuresLabel}>Features</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
            {cap.features.map((f, fi) => (
              <View key={fi} style={[s.featureChip, { borderColor: cap.color + '30', backgroundColor: cap.color + '08' }]}>
                <MaterialCommunityIcons name="check-circle" size={11} color={cap.color} />
                <Text style={[s.featureChipText, { color: cap.color }]}>{f}</Text>
              </View>
            ))}
          </View>
          <Text style={s.featuresLabel}>Try these prompts</Text>
          {cap.prompts.map((prompt, pi) => (
            <TouchableOpacity key={pi} style={s.promptRow} onPress={() => { setActiveTab('chat'); setTimeout(() => sendMessage(prompt), 300); }}>
              <MaterialCommunityIcons name="send-circle" size={16} color={cap.color} />
              <Text style={[s.promptText, { color: cap.color }]}>{prompt}</Text>
            </TouchableOpacity>
          ))}
        </View>
      ))}
    </ScrollView>
  );

  // ── MODALS ──────────────────────────────────────────────────────────────────
  const renderSettingsModal = () => (
    <Modal visible={showSettingsModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowSettingsModal(false)}>
      <SafeAreaView style={{ flex: 1, backgroundColor: C.bg }}>
        <View style={s.modalHeader}>
          <TouchableOpacity style={s.modalClose} onPress={() => setShowSettingsModal(false)}><MaterialCommunityIcons name="close" size={22} color={C.sub} /></TouchableOpacity>
          <Text style={s.modalTitle}>AI Agent Settings</Text>
          <TouchableOpacity onPress={saveSettings}><Text style={{ color: C.purple, fontWeight: '700', fontSize: 15 }}>Save</Text></TouchableOpacity>
        </View>
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16, paddingBottom: 40 }}>
          {/* Personality */}
          <Text style={s.settingGroupTitle}>PERSONALITY</Text>
          <View style={s.settingCard}>
            {Object.entries(PERSONALITY_ICONS).map(([personality, icon]) => (
              <TouchableOpacity key={personality} style={[s.settingOption, { borderBottomColor: C.line }]} onPress={() => setAgentPersonality(personality)}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                  <View style={[s.settingIconBox, { backgroundColor: agentPersonality === personality ? C.purpleLight : C.muted }]}>
                    <MaterialCommunityIcons name={icon} size={16} color={agentPersonality === personality ? C.purple : C.sub} />
                  </View>
                  <Text style={[s.settingOptionText, agentPersonality === personality && { color: C.purple, fontWeight: '700' }]}>
                    {personality.charAt(0).toUpperCase() + personality.slice(1)}
                  </Text>
                </View>
                <MaterialCommunityIcons name={agentPersonality === personality ? 'radiobox-marked' : 'radiobox-blank'} size={20} color={C.purple} />
              </TouchableOpacity>
            ))}
          </View>

          {/* Response length */}
          <Text style={s.settingGroupTitle}>RESPONSE LENGTH</Text>
          <View style={s.settingCard}>
            {[{ id: 'concise', label: 'Concise', sub: 'Short, direct answers' }, { id: 'balanced', label: 'Balanced', sub: 'Moderate detail' }, { id: 'detailed', label: 'Detailed', sub: 'Full explanations' }].map(opt => (
              <TouchableOpacity key={opt.id} style={[s.settingOption, { borderBottomColor: C.line }]} onPress={() => setResponseLength(opt.id)}>
                <View>
                  <Text style={[s.settingOptionText, responseLength === opt.id && { color: C.purple, fontWeight: '700' }]}>{opt.label}</Text>
                  <Text style={{ fontSize: 11, color: C.sub }}>{opt.sub}</Text>
                </View>
                <MaterialCommunityIcons name={responseLength === opt.id ? 'radiobox-marked' : 'radiobox-blank'} size={20} color={C.purple} />
              </TouchableOpacity>
            ))}
          </View>

          {/* Toggles */}
          <Text style={s.settingGroupTitle}>FEATURES</Text>
          <View style={s.settingCard}>
            {[
              { k: 'voice', l: 'Voice Responses', sub: 'Text-to-speech AI replies', val: voiceEnabled, set: setVoiceEnabled, icon: 'microphone', color: C.blue },
              { k: 'notif', l: 'Push Notifications', sub: 'Alerts for insights & automation', val: notificationsEnabled, set: setNotificationsEnabled, icon: 'bell', color: C.orange },
              { k: 'suggest', l: 'Auto Suggestions', sub: 'Context-aware prompt chips', val: autoSuggest, set: setAutoSuggest, icon: 'lightning-bolt', color: C.purple },
              { k: 'ts', l: 'Show Timestamps', sub: 'Message time in chat', val: showTimestamps, set: setShowTimestamps, icon: 'clock', color: C.mint },
            ].map((t, i) => (
              <View key={t.k} style={[s.settingToggleRow, i < 3 && { borderBottomWidth: 1, borderBottomColor: C.line }]}>
                <View style={[s.settingIconBox, { backgroundColor: t.color + '15' }]}><MaterialCommunityIcons name={t.icon} size={16} color={t.color} /></View>
                <View style={{ flex: 1 }}>
                  <Text style={s.settingOptionText}>{t.l}</Text>
                  <Text style={{ fontSize: 11, color: C.sub }}>{t.sub}</Text>
                </View>
                <Switch value={t.val} onValueChange={t.set} trackColor={{ false: C.line, true: C.purple + '60' }} thumbColor={t.val ? C.purple : C.sub} style={{ transform: [{ scaleX: 0.85 }, { scaleY: 0.85 }] }} />
              </View>
            ))}
          </View>

          {/* Danger zone */}
          <Text style={s.settingGroupTitle}>DATA</Text>
          <View style={s.settingCard}>
            <TouchableOpacity style={[s.settingOption, { borderBottomColor: C.line }]} onPress={exportChat}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}><View style={[s.settingIconBox, { backgroundColor: C.blue + '15' }]}><MaterialCommunityIcons name="export" size={16} color={C.blue} /></View><Text style={s.settingOptionText}>Export Chat History</Text></View>
              <MaterialCommunityIcons name="chevron-right" size={18} color={C.line} />
            </TouchableOpacity>
            <TouchableOpacity style={s.settingOption} onPress={clearChat}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}><View style={[s.settingIconBox, { backgroundColor: C.red + '15' }]}><MaterialCommunityIcons name="delete" size={16} color={C.red} /></View><Text style={[s.settingOptionText, { color: C.red }]}>Clear Chat History</Text></View>
              <MaterialCommunityIcons name="chevron-right" size={18} color={C.line} />
            </TouchableOpacity>
          </View>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  const renderAutomationModal = () => {
    const isCreate = isCreatingAutomation;
    const form = isCreate ? newAutomationForm : (selectedAutomation || {});
    const setForm = isCreate ? setNewAutomationForm : () => {};
    return (
      <Modal visible={showAutomationModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowAutomationModal(false)}>
        <SafeAreaView style={{ flex: 1, backgroundColor: C.bg }}>
          <View style={s.modalHeader}>
            <TouchableOpacity style={s.modalClose} onPress={() => setShowAutomationModal(false)}><MaterialCommunityIcons name="close" size={22} color={C.sub} /></TouchableOpacity>
            <Text style={s.modalTitle}>{isCreate ? 'New Automation' : form.name}</Text>
            {isCreate && <TouchableOpacity onPress={saveNewAutomation}><Text style={{ color: C.purple, fontWeight: '700', fontSize: 15 }}>Create</Text></TouchableOpacity>}
            {!isCreate && <TouchableOpacity onPress={() => runAutomationNow(form)}><Text style={{ color: C.mint, fontWeight: '700', fontSize: 15 }}>Run</Text></TouchableOpacity>}
          </View>
          <ScrollView style={{ flex: 1, padding: 16 }}>
            {isCreate ? (
              <>
                {[{ k: 'name', l: 'Name *', i: 'tag', ph: 'e.g. Weekly Sales Report' }, { k: 'description', l: 'Description', i: 'text-short', ph: 'What does this automation do?' }, { k: 'triggerDetail', l: 'Trigger Detail', i: 'lightning-bolt', ph: 'e.g. Every Monday 09:00' }, { k: 'actionDetail', l: 'Action Detail', i: 'play', ph: 'e.g. Send email to sales@company.com' }, { k: 'module', l: 'Module', i: 'apps', ph: 'e.g. Finance, CRM, HR' }].map(f => (
                  <View key={f.k} style={{ marginBottom: 14 }}>
                    <Text style={s.fieldLabel}>{f.l}</Text>
                    <View style={[s.fieldBox, { borderColor: newAutomationForm[f.k] ? C.purple : C.line }]}>
                      <MaterialCommunityIcons name={f.i} size={15} color={C.sub} />
                      <TextInput value={newAutomationForm[f.k]} onChangeText={v => setNewAutomationForm(p => ({ ...p, [f.k]: v }))} placeholder={f.ph} placeholderTextColor={C.sub} style={[s.fieldInput]} />
                    </View>
                  </View>
                ))}
                <Text style={s.fieldLabel}>Trigger Type</Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                  {TRIGGER_TYPES.map(t => (
                    <TouchableOpacity key={t.id} style={[s.typeChip, newAutomationForm.trigger === t.id && { backgroundColor: C.purple, borderColor: C.purple }]} onPress={() => setNewAutomationForm(p => ({ ...p, trigger: t.id }))}>
                      <MaterialCommunityIcons name={t.icon} size={13} color={newAutomationForm.trigger === t.id ? '#fff' : C.sub} />
                      <Text style={[s.typeChipText, newAutomationForm.trigger === t.id && { color: '#fff' }]}>{t.label}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
                <Text style={s.fieldLabel}>Action Type</Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 24 }}>
                  {ACTION_TYPES.map(a => (
                    <TouchableOpacity key={a.id} style={[s.typeChip, newAutomationForm.action === a.id && { backgroundColor: C.purple, borderColor: C.purple }]} onPress={() => setNewAutomationForm(p => ({ ...p, action: a.id }))}>
                      <MaterialCommunityIcons name={a.icon} size={13} color={newAutomationForm.action === a.id ? '#fff' : C.sub} />
                      <Text style={[s.typeChipText, newAutomationForm.action === a.id && { color: '#fff' }]}>{a.label}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
                <TouchableOpacity style={[s.createBtn, { backgroundColor: C.purple }]} onPress={saveNewAutomation}>
                  <MaterialCommunityIcons name="plus" size={18} color="#fff" />
                  <Text style={s.createBtnText}>Create Automation</Text>
                </TouchableOpacity>
              </>
            ) : (
              <>
                {/* Automation detail view */}
                <View style={[s.card, { borderLeftColor: form.color, borderLeftWidth: 4, marginBottom: 16 }]}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <View style={[s.cardIcon, { backgroundColor: form.color + '18' }]}><MaterialCommunityIcons name={form.icon} size={24} color={form.color} /></View>
                    <View style={{ flex: 1 }}>
                      <Text style={s.cardTitle}>{form.name}</Text>
                      <Text style={s.cardSub}>{form.module} Module</Text>
                    </View>
                    <View style={[s.statusBadge, { backgroundColor: (form.status === 'active' ? C.mint : C.orange) + '20' }]}>
                      <Text style={[s.statusBadgeText, { color: form.status === 'active' ? C.mint : C.orange }]}>{form.status}</Text>
                    </View>
                  </View>
                  <Text style={[s.cardSub, { marginBottom: 14 }]}>{form.description}</Text>
                  {[
                    { l: 'Trigger', v: form.triggerDetail, i: 'lightning-bolt' },
                    { l: 'Action', v: form.actionDetail, i: 'play-circle' },
                    { l: 'Last Run', v: form.lastRun, i: 'history' },
                    { l: 'Next Run', v: form.nextRun, i: 'alarm' },
                    { l: 'Total Runs', v: `${form.runsTotal} (${form.runsSuccess} succeeded)`, i: 'counter' },
                    { l: 'Time Saved', v: `${form.savedHours}h per week`, i: 'timer-sand' },
                  ].map((f_, fi) => (
                    <View key={fi} style={[s.detailRow, { borderBottomColor: C.line, borderBottomWidth: fi < 5 ? 1 : 0 }]}>
                      <MaterialCommunityIcons name={f_.i} size={14} color={C.sub} style={{ marginRight: 8, width: 18 }} />
                      <Text style={s.detailLabel}>{f_.l}</Text>
                      <Text style={s.detailValue}>{f_.v}</Text>
                    </View>
                  ))}
                </View>
                {/* Success rate bar */}
                <Text style={s.fieldLabel}>Success Rate</Text>
                <View style={{ height: 10, borderRadius: 5, backgroundColor: C.line, overflow: 'hidden', marginBottom: 20 }}>
                  <View style={{ height: '100%', width: `${form.runsTotal > 0 ? (form.runsSuccess / form.runsTotal) * 100 : 0}%`, backgroundColor: C.mint, borderRadius: 5 }} />
                </View>
                <View style={{ flexDirection: 'row', gap: 10, marginBottom: 20 }}>
                  <TouchableOpacity style={[s.cardActionBtn, { flex: 1, padding: 14, borderColor: C.mint + '40', backgroundColor: C.mint + '10', justifyContent: 'center' }]} onPress={() => { setShowAutomationModal(false); runAutomationNow(form); }}>
                    <MaterialCommunityIcons name="play" size={16} color={C.mint} />
                    <Text style={[s.cardActionText, { color: C.mint, fontSize: 14 }]}>Run Now</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={[s.cardActionBtn, { flex: 1, padding: 14, borderColor: (form.status === 'active' ? C.orange : C.mint) + '40', backgroundColor: (form.status === 'active' ? C.orange : C.mint) + '10', justifyContent: 'center' }]} onPress={() => { toggleAutomation(form.id); setShowAutomationModal(false); }}>
                    <MaterialCommunityIcons name={form.status === 'active' ? 'pause' : 'play'} size={16} color={form.status === 'active' ? C.orange : C.mint} />
                    <Text style={[s.cardActionText, { color: form.status === 'active' ? C.orange : C.mint, fontSize: 14 }]}>{form.status === 'active' ? 'Pause' : 'Resume'}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={[s.cardActionBtn, { flex: 1, padding: 14, borderColor: C.red + '40', backgroundColor: C.red + '10', justifyContent: 'center' }]} onPress={() => { deleteAutomation(form.id); setShowAutomationModal(false); }}>
                    <MaterialCommunityIcons name="delete" size={16} color={C.red} />
                    <Text style={[s.cardActionText, { color: C.red, fontSize: 14 }]}>Delete</Text>
                  </TouchableOpacity>
                </View>
              </>
            )}
          </ScrollView>
        </SafeAreaView>
      </Modal>
    );
  };

  const renderInsightDetailModal = () => {
    if (!selectedInsight) return null;
    const typeColors = { positive: C.mint, warning: C.orange, negative: C.red, info: C.blue };
    const tColor = typeColors[selectedInsight.type] || C.sub;
    return (
      <Modal visible={showInsightDetail} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowInsightDetail(false)}>
        <SafeAreaView style={{ flex: 1, backgroundColor: C.bg }}>
          <LinearGradient colors={[tColor, tColor + 'bb']} style={{ flexDirection: 'row', alignItems: 'center', padding: 16, gap: 12 }}>
            <TouchableOpacity style={[s.modalClose, { backgroundColor: 'rgba(255,255,255,0.2)' }]} onPress={() => setShowInsightDetail(false)}><MaterialCommunityIcons name="close" size={22} color="#fff" /></TouchableOpacity>
            <MaterialCommunityIcons name={selectedInsight.icon} size={24} color="#fff" />
            <Text style={{ flex: 1, color: '#fff', fontSize: 15, fontWeight: '800' }} numberOfLines={2}>{selectedInsight.title}</Text>
          </LinearGradient>
          <ScrollView style={{ flex: 1, padding: 16 }}>
            <View style={[s.card, { borderTopColor: tColor, borderTopWidth: 3 }]}>
              {[
                { l: 'Category', v: selectedInsight.category },
                { l: 'Type', v: selectedInsight.type },
                { l: 'Priority', v: selectedInsight.priority },
                { l: 'Confidence', v: `${selectedInsight.confidence}%` },
                { l: 'Impact', v: selectedInsight.impact },
                { l: 'Generated', v: selectedInsight.timestamp },
              ].map((f, i) => (
                <View key={i} style={[s.detailRow, { borderBottomColor: C.line, borderBottomWidth: i < 5 ? 1 : 0 }]}>
                  <Text style={s.detailLabel}>{f.l}</Text>
                  <Text style={s.detailValue}>{f.v}</Text>
                </View>
              ))}
            </View>
            <View style={s.card}>
              <Text style={[s.fieldLabel, { marginBottom: 8 }]}>Analysis</Text>
              <Text style={{ fontSize: 14, color: C.text, lineHeight: 22 }}>{selectedInsight.description}</Text>
            </View>
            <View style={[s.card, { borderColor: C.purple + '30', backgroundColor: C.purple + '05' }]}>
              <Text style={[s.fieldLabel, { color: C.purple, marginBottom: 8 }]}>💡 Recommendation</Text>
              <Text style={{ fontSize: 14, color: C.text, lineHeight: 22 }}>{selectedInsight.recommendation}</Text>
            </View>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 30 }}>
              {(selectedInsight.actions || []).map((action, ai) => (
                <TouchableOpacity key={ai} style={[s.cardActionBtn, { flex: 1, padding: 14, borderColor: tColor + '40', backgroundColor: tColor + '10', justifyContent: 'center' }]} onPress={() => { setShowInsightDetail(false); setActiveTab('chat'); setTimeout(() => sendMessage(`Help me with: ${action} regarding: ${selectedInsight.title}`), 300); }}>
                  <Text style={[s.cardActionText, { color: tColor, fontSize: 13 }]}>{action}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </ScrollView>
        </SafeAreaView>
      </Modal>
    );
  };

  // ─── MAIN RETURN ──────────────────────────────────────────────────────────
  const renderContent = () => {
    switch (activeTab) {
      case 'chat': return renderChat();
      case 'automations': return renderAutomations();
      case 'insights': return renderInsights();
      case 'capabilities': return renderCapabilities();
      default: return renderChat();
    }
  };

  return (
    <SafeAreaView style={s.root} edges={['bottom']}>
      <Animated.View style={{ flex: 1, opacity: fadeAnim, transform: [{ translateY: slideAnim }] }}>
        {renderHeader()}
        {renderTabs()}
        {renderContent()}
      </Animated.View>
      {renderSettingsModal()}
      {renderAutomationModal()}
      {renderInsightDetailModal()}
    </SafeAreaView>
  );
};

// ─── STYLES ───────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  root: { flex: 1, backgroundColor: C.bg },

  // Header
  header: { paddingHorizontal: 16, paddingTop: 42, paddingBottom: 0 },
  headerRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 14 },
  headerIconBtn: { width: 36, height: 36, borderRadius: 18, backgroundColor: 'rgba(255,255,255,0.2)', justifyContent: 'center', alignItems: 'center' },
  headerCenter: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 10 },
  headerTitle: { color: '#fff', fontSize: 15, fontWeight: '800' },
  headerSub: { color: 'rgba(255,255,255,0.7)', fontSize: 10 },
  avatarWrap: { position: 'relative' },
  avatar: { width: 42, height: 42, borderRadius: 21, justifyContent: 'center', alignItems: 'center' },
  statusDot: { position: 'absolute', bottom: 1, right: 1, width: 11, height: 11, borderRadius: 5.5, borderWidth: 2, borderColor: C.purpleDark },
  statusDotSmall: { width: 6, height: 6, borderRadius: 3 },
  statusPill: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 7, paddingVertical: 2, borderRadius: 8 },
  statusPillText: { fontSize: 10, fontWeight: '700', textTransform: 'capitalize' },
  statChip: { alignItems: 'center', marginRight: 16, paddingBottom: 8 },
  statVal: { color: '#fff', fontSize: 15, fontWeight: '900', marginTop: 2 },
  statLabel: { color: 'rgba(255,255,255,0.65)', fontSize: 9, marginTop: 1 },

  // Tabs
  tabRow: { flexDirection: 'row', backgroundColor: C.surface, borderBottomWidth: 1, borderBottomColor: C.line },
  tab: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 11, gap: 4, position: 'relative' },
  tabActive: { borderBottomWidth: 2.5, borderBottomColor: C.purple },
  tabText: { fontSize: 11, fontWeight: '600', color: C.sub },
  tabTextActive: { color: C.purple },
  tabBadge: { position: 'absolute', top: 6, right: 6, minWidth: 15, height: 15, borderRadius: 7.5, backgroundColor: C.purple, justifyContent: 'center', alignItems: 'center', paddingHorizontal: 3 },
  tabBadgeText: { color: '#fff', fontSize: 8, fontWeight: '800' },

  // Chat
  msgRow: { flexDirection: 'row', marginBottom: 14, alignItems: 'flex-end' },
  msgRowUser: { justifyContent: 'flex-end' },
  msgRowAI: { justifyContent: 'flex-start' },
  aiAvatar: { width: 28, height: 28, borderRadius: 14, backgroundColor: C.purpleLight, justifyContent: 'center', alignItems: 'center', marginRight: 8, marginBottom: 4 },
  userAvatar: { width: 28, height: 28, borderRadius: 14, backgroundColor: C.purple, justifyContent: 'center', alignItems: 'center', marginLeft: 8, marginBottom: 4 },
  bubble: { borderRadius: 18, paddingHorizontal: 15, paddingVertical: 12 },
  bubbleUser: { backgroundColor: C.purple, borderBottomRightRadius: 4 },
  bubbleAI: { backgroundColor: C.muted, borderBottomLeftRadius: 4 },
  msgTextUser: { color: '#fff', fontSize: 14, lineHeight: 20 },
  msgTextAI: { color: C.text, fontSize: 14, lineHeight: 20 },
  msgTime: { fontSize: 9, color: C.sub },
  confidence: { fontSize: 9, color: C.sub, fontStyle: 'italic' },
  suggestRow: { backgroundColor: C.surface, borderTopWidth: 1, borderTopColor: C.line },
  suggestLabel: { fontSize: 10, fontWeight: '700', color: C.sub, letterSpacing: 0.5, textTransform: 'uppercase' },
  suggestChip: { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: C.purpleLight, paddingHorizontal: 12, paddingVertical: 7, borderRadius: 14, marginRight: 8 },
  suggestText: { fontSize: 11, color: C.purple, fontWeight: '600' },
  inputRow: { flexDirection: 'row', alignItems: 'flex-end', paddingHorizontal: 14, paddingVertical: 10, backgroundColor: C.surface, borderTopWidth: 1, borderTopColor: C.line, gap: 10 },
  inputIconBtn: { width: 38, height: 38, borderRadius: 19, backgroundColor: C.purpleLight, justifyContent: 'center', alignItems: 'center' },
  textInput: { flex: 1, borderWidth: 1, borderColor: C.line, borderRadius: 18, paddingHorizontal: 16, paddingVertical: 10, fontSize: 14, color: C.text, maxHeight: 110, backgroundColor: C.bg },
  sendBtn: { width: 42, height: 42, borderRadius: 21, backgroundColor: C.purple, justifyContent: 'center', alignItems: 'center' },
  sendBtnDisabled: { backgroundColor: C.line },

  // Cards
  card: { backgroundColor: C.surface, borderRadius: 16, borderWidth: 1, borderColor: C.cardBorder, padding: 16, marginBottom: 12, elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4 },
  cardIcon: { width: 46, height: 46, borderRadius: 14, justifyContent: 'center', alignItems: 'center' },
  cardTitle: { fontSize: 14, fontWeight: '700', color: C.text, marginBottom: 3 },
  cardSub: { fontSize: 12, color: C.sub, lineHeight: 18 },
  cardActions: { flexDirection: 'row', gap: 8, marginTop: 14 },
  cardActionBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 5, paddingHorizontal: 12, paddingVertical: 8, borderRadius: 10, borderWidth: 1 },
  cardActionText: { fontSize: 11, fontWeight: '700' },
  metaChip: { flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: C.muted, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 8 },
  metaText: { fontSize: 10, color: C.sub },
  statusBadge: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 10 },
  statusBadgeText: { fontSize: 10, fontWeight: '700', textTransform: 'capitalize' },
  priorityBadge: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 8 },
  priorityText: { fontSize: 10, fontWeight: '700', textTransform: 'capitalize' },
  recommendBox: { padding: 12, borderRadius: 10, borderWidth: 1, marginTop: 10 },
  recommendLabel: { fontSize: 11, fontWeight: '700', color: C.purple, marginBottom: 4 },
  recommendText: { fontSize: 12, color: C.text, lineHeight: 18 },
  timestamp: { fontSize: 10, color: C.sub },
  insightActionBtn: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 10, borderWidth: 1 },
  insightActionText: { fontSize: 10, fontWeight: '700' },

  // Automations
  autoSummary: { flexDirection: 'row', backgroundColor: C.surface, borderBottomWidth: 1, borderBottomColor: C.line, paddingVertical: 14 },
  autoSummaryItem: { flex: 1, alignItems: 'center' },
  autoSummaryVal: { fontSize: 20, fontWeight: '900' },
  autoSummaryLabel: { fontSize: 10, color: C.sub, marginTop: 2 },
  filterChip: { paddingHorizontal: 14, paddingVertical: 7, borderRadius: 16, borderWidth: 1, borderColor: C.line, marginRight: 8 },
  filterChipText: { fontSize: 12, fontWeight: '600', color: C.sub },
  addBtn: { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: C.purple, paddingHorizontal: 14, paddingVertical: 8, borderRadius: 12 },
  addBtnText: { color: '#fff', fontSize: 13, fontWeight: '700' },

  // Capabilities
  capabilitiesHero: { flexDirection: 'row', alignItems: 'center', borderRadius: 20, padding: 20, marginBottom: 16 },
  capTitle: { fontSize: 15, fontWeight: '800', color: C.text, marginBottom: 4 },
  featuresLabel: { fontSize: 11, fontWeight: '700', color: C.sub, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 },
  featureChip: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 10, paddingVertical: 5, borderRadius: 10, borderWidth: 1 },
  featureChipText: { fontSize: 11, fontWeight: '600' },
  promptRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 8, borderTopWidth: 1, borderTopColor: C.line },
  promptText: { fontSize: 13, fontWeight: '600', flex: 1 },

  // Modals
  modalHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, backgroundColor: C.surface, borderBottomWidth: 1, borderBottomColor: C.line },
  modalClose: { width: 34, height: 34, borderRadius: 17, backgroundColor: C.muted, justifyContent: 'center', alignItems: 'center' },
  modalTitle: { fontSize: 16, fontWeight: '800', color: C.text },
  settingGroupTitle: { fontSize: 11, fontWeight: '700', color: C.sub, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8, marginTop: 16, marginLeft: 4 },
  settingCard: { backgroundColor: C.surface, borderRadius: 16, borderWidth: 1, borderColor: C.line, overflow: 'hidden', marginBottom: 4 },
  settingOption: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 14, borderBottomWidth: 1 },
  settingOptionText: { fontSize: 14, color: C.text, fontWeight: '500' },
  settingIconBox: { width: 32, height: 32, borderRadius: 9, justifyContent: 'center', alignItems: 'center' },
  settingToggleRow: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14 },
  fieldLabel: { fontSize: 12, fontWeight: '700', color: C.text, marginBottom: 6 },
  fieldBox: { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: C.surface, borderRadius: 12, borderWidth: 1, padding: 12 },
  fieldInput: { flex: 1, fontSize: 14, color: C.text },
  typeChip: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 12, paddingVertical: 7, borderRadius: 14, borderWidth: 1, borderColor: C.line },
  typeChipText: { fontSize: 12, fontWeight: '600', color: C.sub },
  createBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 16, borderRadius: 16, marginBottom: 40 },
  createBtnText: { color: '#fff', fontSize: 16, fontWeight: '700' },
  detailRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 11 },
  detailLabel: { flex: 1, fontSize: 12, color: C.sub },
  detailValue: { fontSize: 13, fontWeight: '600', color: C.text },
});

export default AIAgentScreen;